/** useRunAgentTest — SSE dry-run streaming hook for owner test-chat preview.
 *
 * Single-turn model: each send() replaces the in-progress stream (cancels
 * any prior stream first). No thread persistence — backend discards all state.
 *
 * Uses direct fetch + shared/api createSseParser for the owner experiment SSE endpoint.
 * The endpoint is POST /api/v1/owner/agents/{agentId}/experiment.
 */

import { useCallback, useRef, useState } from 'react';
import { createSseParser } from '@/shared/api';
import { getToken } from '@/shared/auth';

export interface PreviewMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
}

export interface RunAgentTestOpts {
  promptVersionId: string | null;
  configVersionId?: string | null;
  draftSkillIds?: string[] | null;
  draftSkillVersionIds?: string[] | null;
  message: string;
}

export interface UseRunAgentTestResult {
  send: (opts: RunAgentTestOpts) => void;
  cancel: () => void;
  messages: PreviewMessage[];
  isStreaming: boolean;
  error: string | null;
  clearMessages: () => void;
}

async function runAgentTest(opts: {
  agentId: string;
  prompt: string;
  promptVersionId?: string | null;
  configVersionId?: string | null;
  draftSkillIds?: string[] | null;
  draftSkillVersionIds?: string[] | null;
  onDelta: (text: string) => void;
  onDone?: () => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const body = JSON.stringify({
    prompt: opts.prompt,
    prompt_version_id: opts.promptVersionId ?? null,
    config_version_id: opts.configVersionId ?? null,
    draft_skill_ids: opts.draftSkillIds?.length ? opts.draftSkillIds : null,
    draft_skill_version_ids: opts.draftSkillVersionIds?.length ? opts.draftSkillVersionIds : null,
  });

  const chatPath = `/api/v1/owner/agents/${encodeURIComponent(opts.agentId)}/experiment`;

  let resp: Response;
  try {
    resp = await fetch(chatPath, { method: 'POST', headers, body, signal: opts.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      opts.onDone?.();
      return;
    }
    opts.onError?.(err instanceof Error ? err.message : String(err));
    return;
  }

  if (!resp.ok) {
    const payload = await resp.json().catch(() => ({ detail: resp.statusText })) as { detail?: string };
    opts.onError?.(payload.detail || `HTTP ${resp.status}`);
    return;
  }

  const reader = resp.body?.getReader();
  if (!reader) {
    opts.onError?.('No response body');
    return;
  }

  const decoder = new TextDecoder();
  const parser = createSseParser();

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const events = parser.push(decoder.decode(value, { stream: true }));
      for (const ev of events) {
        if (!ev.data || ev.data === '[DONE]') continue;
        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(ev.data) as Record<string, unknown>;
        } catch {
          // Malformed SSE payload — skip.
          continue;
        }
        const evType = ev.event || (parsed.type as string) || (parsed.event as string) || '';
        if (evType === 'text_delta') {
          const data = parsed.data as Record<string, unknown> | undefined;
          const text = (data?.text ?? parsed.text ?? '') as string;
          if (typeof text === 'string' && text.length > 0) opts.onDelta(text);
        } else if (evType === 'error') {
          const data = parsed.data as Record<string, unknown> | undefined;
          const msg = (data?.message ?? parsed.message ?? 'Agent error') as string;
          opts.onError?.(msg);
        }
      }
    }
    opts.onDone?.();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      opts.onDone?.();
      return;
    }
    opts.onError?.(err instanceof Error ? err.message : String(err));
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Already released.
    }
  }
}

export function useRunAgentTest(agentId: string): UseRunAgentTestResult {
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const send = useCallback(
    (opts: RunAgentTestOpts) => {
      const text = opts.message.trim();
      if (!text) return;

      // Cancel any prior in-flight stream before starting fresh.
      abortRef.current?.abort();

      const userMsg: PreviewMessage = { id: `u-${Date.now()}`, role: 'user', content: text };
      const assistantId = `a-${Date.now()}`;
      const assistantMsg: PreviewMessage = { id: assistantId, role: 'assistant', content: '' };

      setError(null);
      setMessages([userMsg, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      void runAgentTest({
        agentId,
        prompt: text,
        promptVersionId: opts.promptVersionId,
        configVersionId: opts.configVersionId,
        draftSkillIds: opts.draftSkillIds,
        draftSkillVersionIds: opts.draftSkillVersionIds,
        signal: controller.signal,
        onDelta: (chunk) => {
          setMessages((prev) => {
            const next = [...prev];
            const idx = next.findIndex((m) => m.id === assistantId);
            if (idx !== -1) {
              next[idx] = { ...next[idx], content: next[idx].content + chunk };
            }
            return next;
          });
        },
        onError: (msg) => {
          setError(msg);
          setMessages((prev) => {
            const without = prev.filter((m) => m.id !== assistantId);
            return [...without, { id: `e-${Date.now()}`, role: 'error', content: msg }];
          });
          setIsStreaming(false);
          abortRef.current = null;
        },
        onDone: () => {
          setIsStreaming(false);
          abortRef.current = null;
        },
      });
    },
    [agentId],
  );

  return { send, cancel, messages, isStreaming, error, clearMessages };
}
